import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import { getAccessToken } from "../../awsAuth";
import { useNavigate } from "react-router-dom";
import "./CommentsModal.scss";
import Alerts from "../Alerts/Alerts";
import { useToast } from "../Alerts/Toast";
import deleteIcon from "./img/delete.png";
import sendArrow from "./img/SendArrow.png";

const DEFAULT_AVATAR =
  "https://cvanto-photos.s3.us-east-1.amazonaws.com/DefaultAvatar/avatar.png";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5000";

function timeAgo(ts) {
  const now = Date.now();
  const diff = Math.max(
    0,
    now - (typeof ts === "number" ? ts : +new Date(ts))
  );
  const m = Math.floor(diff / 60000);
  if (m < 1) return "щойно";
  if (m < 60) return `${m} хв тому`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} год тому`;
  const d = Math.floor(h / 24);
  if (d === 1) return "вчора";
  if (d < 7) return `${d} дн тому`;
  return new Date(ts).toLocaleDateString();
}

function useBodyScrollLock(lock) {
  useEffect(() => {
    const { body, documentElement: html } = document;

    const prev = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      paddingRight: body.style.paddingRight,
    };

    if (lock) {
      const scrollY = window.scrollY || window.pageYOffset || 0;
      const scrollbarW = window.innerWidth - html.clientWidth;

      if (scrollbarW > 0) body.style.paddingRight = `${scrollbarW}px`;

      body.classList.add("cv-modal-open");
      html.classList.add("cv-scroll-lock");

      body.style.position = "fixed";
      body.style.top = `-${scrollY}px`;
      body.style.width = "100%";
      body.style.overflow = "hidden";

      return () => {
        body.style.overflow = prev.overflow;
        body.style.position = prev.position;
        body.style.top = prev.top;
        body.style.width = prev.width;
        body.style.paddingRight = prev.paddingRight;

        body.classList.remove("cv-modal-open");
        html.classList.remove("cv-scroll-lock");

        const y = Math.abs(parseInt(prev.top || "0")) || scrollY;
        window.scrollTo(0, y);
      };
    }

    return () => {};
  }, [lock]);
}

function groupByParent(items) {
  const map = new Map();

  items.forEach((it) => {
    const p = it.parentId || "root";
    if (!map.has(p)) map.set(p, []);
    map.get(p).push(it);
  });

  for (const arr of map.values()) {
    arr.sort((a, b) => a.createdAt - b.createdAt);
  }

  return map;
}

function isLongText(text) {
  if (!text) return false;
  const len = text.length;
  const lines = text.split(/\r?\n/).length;
  return len > 280 || lines > 6;
}

function normalizeComment(c) {
  return {
    ...c,
    createdAt:
      typeof c.createdAt === "number"
        ? c.createdAt
        : new Date(c.createdAt).getTime() || Date.now(),
    parentId: c.parentId || null,
  };
}

function dedupeComments(items) {
  const map = new Map();

  items.forEach((it) => {
    if (!it?.commentId) return;
    map.set(it.commentId, normalizeComment(it));
  });

  return Array.from(map.values()).sort((a, b) => a.createdAt - b.createdAt);
}

function CommentRow({ c, me, listingAuthorId, onReply, onDelete, index = 0 }) {
  const [expanded, setExpanded] = useState(false);

  const ownerId = listingAuthorId || c.listingOwnerId || c.contentOwnerId || null;
  const canDelete = !!me && (me === c.userId || (ownerId && me === ownerId));
  const isListingAuthor = ownerId && c.userId === ownerId;
  const long = isLongText(c.text);

  const navigate = useNavigate();

  const goToProfile = () => {
    if (c.userId) navigate(`/profile/${c.userId}`);
  };

  return (
    <div
      className={`cm-item ${c.deleted ? "cm-item--deleted" : ""} cm-appear`}
      style={{ "--stagger": `${index * 18}ms` }}
    >
      <img
        className="cm-item__avatar cm-clickable"
        src={c.authorSnapshot?.avatar || DEFAULT_AVATAR}
        alt=""
        onClick={goToProfile}
        loading="lazy"
        decoding="async"
      />

      <div className="cm-item__main">
        <div className="cm-item__top">
          <div className="cm-item__metaLeft">
            <b className="cm-item__user cm-clickable" onClick={goToProfile}>
              {c.authorSnapshot?.username || "Користувач"}
            </b>

            {isListingAuthor && (
              <span className="cm-chip cm-chip--owner">Автор публікації</span>
            )}

            <span className="cm-item__dot">•</span>
            <span className="cm-item__time">{timeAgo(c.createdAt)}</span>
          </div>

          {canDelete && !c.deleted && (
            <button
              className="cm-delete"
              onClick={() => onDelete?.(c)}
              aria-label="Видалити"
            >
              <img src={deleteIcon} alt="" />
            </button>
          )}
        </div>

        <div
          className={`cm-item__text ${
            !expanded && long ? "cm-item__text--clamp" : ""
          }`}
        >
          {c.text}
        </div>

        <div className="cm-item__actions">
          {!c.deleted && (
            <button className="cm-link" onClick={() => onReply(c)}>
              Відповісти
            </button>
          )}

          {long && (
            <button className="cm-link" onClick={() => setExpanded((v) => !v)}>
              {expanded ? "Згорнути" : "Показати повністю"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CommentsModal({
  open,
  listingId,
  listingAuthorId,
  onClose,
  onCountChange,
}) {
  const { userId } = useAuth();
  const toast = useToast();

  useBodyScrollLock(open);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const pendingDeleteRef = useRef(null);

  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 640px)").matches
      : false
  );

  const [sheetState, setSheetState] = useState("half");
  const sheetRef = useRef(null);
  const headerRef = useRef(null);

  const dragRef = useRef({
    startY: 0,
    startH: 0,
    dragging: false,
    lastDelta: 0,
  });

  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState(null);

  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState(null);

  const [meAvatar, setMeAvatar] = useState(DEFAULT_AVATAR);

  const inputRef = useRef(null);
  const scrollerRef = useRef(null);

  const tree = useMemo(() => groupByParent(items), [items]);
  const roots = tree.get("root") || [];

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mm = window.matchMedia("(max-width: 640px)");
    const fn = () => setIsMobile(mm.matches);

    if (mm.addEventListener) mm.addEventListener("change", fn);
    else mm.addListener(fn);

    return () => {
      if (mm.removeEventListener) mm.removeEventListener("change", fn);
      else mm.removeListener(fn);
    };
  }, []);

  useEffect(() => {
    let ignore = false;

    const loadMe = async () => {
      if (!userId) return;

      try {
        const token = await getAccessToken().catch(() => null);

        const res = await fetch(`${API_BASE}/api/account/${userId}`, {
          cache: "no-store",
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!res.ok) return;

        const u = await res.json();

        if (!ignore) {
          setMeAvatar(u?.avatar || u?.avatarUrl || DEFAULT_AVATAR);
        }
      } catch {}
    };

    loadMe();

    return () => {
      ignore = true;
    };
  }, [userId]);

  const reset = useCallback(() => {
    setItems([]);
    setNextCursor(null);
    setText("");
    setReplyTo(null);
    setError(null);
    setSheetState("half");
  }, []);

  const fetchAllComments = useCallback(async () => {
    if (!listingId) return;

    setLoading(true);
    setError(null);

    try {
      let cursor = null;
      let all = [];
      let safety = 0;

      do {
        const url = cursor
          ? `${API_BASE}/api/comments/list/${listingId}?limit=100&cursor=${encodeURIComponent(cursor)}`
          : `${API_BASE}/api/comments/list/${listingId}?limit=100`;

        const { data } = await axios.get(url);

        all = dedupeComments([
          ...(all || []),
          ...((data?.items || []).map(normalizeComment)),
        ]);

        cursor = data?.nextCursor || null;

        safety += 1;
        if (safety > 20) break;
      } while (cursor);

      setItems(all);
      setNextCursor(null);

      requestAnimationFrame(() => {
        scrollerRef.current?.scrollTo({
          top: scrollerRef.current.scrollHeight,
          behavior: "auto",
        });
      });
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "Помилка завантаження");
    } finally {
      setLoading(false);
    }
  }, [listingId]);

  useEffect(() => {
    if (!open) return;
    reset();
  }, [open, reset]);

  useEffect(() => {
    if (open && listingId) fetchAllComments().catch(() => {});
  }, [open, listingId, fetchAllComments]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;

    const MAX = 120;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, MAX) + "px";
  }, [text]);

  const handlePost = useCallback(async () => {
    const t = text.trim();
    if (!t || !listingId) return;

    setPosting(true);
    setError(null);

    const payload = {
      text: t,
      parentId: replyTo?.id || null,
    };

    try {
      const token = await getAccessToken().catch(() => null);

      if (!token) {
        throw new Error("Потрібна авторизація");
      }

      await axios.post(`${API_BASE}/api/comments/${listingId}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setText("");
      setReplyTo(null);
      onCountChange?.(1);

      await fetchAllComments();

      try {
        window.dispatchEvent(
          new CustomEvent("cv-comments-updated", {
            detail: { listingId, delta: 1 },
          })
        );
      } catch {}

      requestAnimationFrame(() => {
        scrollerRef.current?.scrollTo({
          top: scrollerRef.current.scrollHeight,
          behavior: "smooth",
        });
      });
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "Помилка надсилання");
    } finally {
      setPosting(false);
    }
  }, [text, listingId, replyTo, onCountChange, fetchAllComments]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handlePost();
    }
  };

  const onReply = useCallback((c) => {
    setReplyTo({
      id: c.commentId,
      username: c.authorSnapshot?.username || "Користувач",
    });

    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  }, []);

  const onDelete = useCallback((c) => {
    pendingDeleteRef.current = c;
    setConfirmOpen(true);
  }, []);

  const runConfirmedDelete = useCallback(async () => {
    const c = pendingDeleteRef.current;

    pendingDeleteRef.current = null;
    setConfirmOpen(false);

    if (!c?.commentId) return;

    try {
      setError(null);

      const token = await getAccessToken().catch(() => null);

      if (!token) {
        throw new Error("Потрібна авторизація");
      }

      const { data } = await axios.delete(
        `${API_BASE}/api/comments/${c.commentId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const deletedIds = data?.deletedIds || [c.commentId];

      onCountChange?.(-deletedIds.length);
      await fetchAllComments();

      toast.success("Коментар видалено");

      try {
        window.dispatchEvent(
          new CustomEvent("cv-comments-updated", {
            detail: { listingId, delta: -deletedIds.length },
          })
        );
      } catch {}
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || "Не вдалося видалити";
      setError(msg);
      toast.error(String(msg), 3200);
    }
  }, [onCountChange, listingId, toast, fetchAllComments]);

  const HALF_VH = 58;
  const FULL_VH = 100;

  useEffect(() => {
    if (!isMobile || !open) return;

    const sheet = sheetRef.current;

    if (sheet) {
      sheet.style.setProperty(
        "--cm-sheet-height",
        `${sheetState === "full" ? FULL_VH : HALF_VH}vh`
      );
    }
  }, [isMobile, open, sheetState]);

  const requestCloseRef = useRef(null);
  const [isClosing, setIsClosing] = useState(false);

  const requestClose = useCallback(() => {
    if (isClosing) return;

    setIsClosing(true);

    setTimeout(() => {
      setIsClosing(false);
      onClose?.();
    }, 200);
  }, [isClosing, onClose]);

  requestCloseRef.current = requestClose;

  const onGrabStart = (clientY) => {
    const sheet = sheetRef.current;
    if (!sheet) return;

    dragRef.current = {
      startY: clientY,
      startH: sheetState === "full" ? FULL_VH : HALF_VH,
      dragging: true,
      lastDelta: 0,
    };

    sheet.classList.add("cm-modal--dragging");
  };

  const onGrabMove = (clientY) => {
    if (!dragRef.current.dragging) return;

    const deltaY = clientY - dragRef.current.startY;
    dragRef.current.lastDelta = deltaY;

    const screenH = Math.max(window.innerHeight, 1);
    const deltaVH = (deltaY / screenH) * 100;

    let nextVH = dragRef.current.startH - deltaVH;
    nextVH = Math.max(40, Math.min(100, nextVH));

    const sheet = sheetRef.current;
    if (sheet) sheet.style.setProperty("--cm-sheet-height", `${nextVH}vh`);
  };

  const onGrabEnd = () => {
    if (!dragRef.current.dragging) return;

    dragRef.current.dragging = false;

    const delta = dragRef.current.lastDelta;
    const sheet = sheetRef.current;

    if (sheet) sheet.classList.remove("cm-modal--dragging");

    const CLOSE_DRAG_PX = 90;
    const SNAP_DRAG_PX = 70;

    if (sheetState === "half") {
      if (delta > CLOSE_DRAG_PX) {
        requestCloseRef.current?.();
        return;
      }

      if (-delta > SNAP_DRAG_PX) {
        setSheetState("full");
        requestAnimationFrame(() =>
          sheet?.style.setProperty("--cm-sheet-height", `${FULL_VH}vh`)
        );
        return;
      }

      requestAnimationFrame(() =>
        sheet?.style.setProperty("--cm-sheet-height", `${HALF_VH}vh`)
      );
    } else {
      if (delta > SNAP_DRAG_PX) {
        setSheetState("half");
        requestAnimationFrame(() =>
          sheet?.style.setProperty("--cm-sheet-height", `${HALF_VH}vh`)
        );
        return;
      }

      requestAnimationFrame(() =>
        sheet?.style.setProperty("--cm-sheet-height", `${FULL_VH}vh`)
      );
    }
  };

  useEffect(() => {
    if (!isMobile || !open) return;

    const el = headerRef.current;
    if (!el) return;

    const onPointerDown = (e) => {
      e.preventDefault();

      const y = e.touches ? e.touches[0].clientY : e.clientY;

      onGrabStart(y);

      window.addEventListener("pointermove", onPointerMove, {
        passive: false,
      });
      window.addEventListener("pointerup", onPointerUp, { passive: false });
      window.addEventListener("touchmove", onTouchMove, { passive: false });
      window.addEventListener("touchend", onTouchEnd, { passive: false });
    };

    const onPointerMove = (e) => onGrabMove(e.clientY);

    const onPointerUp = () => {
      onGrabEnd();

      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };

    const onTouchMove = (e) => onGrabMove(e.touches[0].clientY);

    const onTouchEnd = () => {
      onGrabEnd();

      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
    };

    el.addEventListener("pointerdown", onPointerDown, { passive: false });
    el.addEventListener("touchstart", onPointerDown, { passive: false });

    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("touchstart", onPointerDown);

      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [isMobile, open, sheetState]);

  if (!open) return null;

  const onBackdropClick = (e) => {
    if (e.target === e.currentTarget) requestCloseRef.current?.();
  };

  const hasText = Boolean(text.trim());

  const renderCommentBranch = (comment, depth = 0, index = 0) => {
    const replies = tree.get(comment.commentId) || [];

    return (
      <div
        key={comment.commentId}
        className={`cm-thread cm-appear ${depth > 0 ? "cm-thread--nested" : ""}`}
        style={{ "--stagger": `${index * 18}ms` }}
      >
        <CommentRow
          c={comment}
          me={userId}
          listingAuthorId={listingAuthorId}
          onReply={onReply}
          onDelete={onDelete}
          index={index}
        />

        {replies.length > 0 && (
          <div className="cm-replies">
            {replies.map((reply, replyIndex) =>
              renderCommentBranch(reply, depth + 1, replyIndex + 1)
            )}
          </div>
        )}
      </div>
    );
  };

  const modalNode = (
    <div
      className={`cm-backdrop ${isMobile ? "is-mobile" : ""} ${
        isClosing ? "is-closing" : "is-opening"
      }`}
      onClick={onBackdropClick}
    >
      <div
        ref={sheetRef}
        className={`cm-modal ${isMobile ? `is-${sheetState}` : ""} ${
          isClosing ? "is-closing" : "is-opening"
        }`}
        role="dialog"
        aria-modal="true"
      >
        <div ref={headerRef} className="cm-header">
          <div className="cm-grabber" aria-hidden />
          <div className="cm-title">Коментарі</div>

          {!isMobile && (
            <button
              className="cm-close"
              onClick={() => requestCloseRef.current?.()}
              aria-label="Закрити"
            >
              ✕
            </button>
          )}
        </div>

        {!listingId ? (
          <div className="cm-content cm-content--error" ref={scrollerRef}>
            <div className="cm-error">
              ⚠️ Не знайдено <code>listingId</code>.
            </div>
          </div>
        ) : (
          <div className="cm-content" ref={scrollerRef}>
            {error && <div className="cm-error">⚠️ {error}</div>}

            {!error && items.length === 0 && !loading ? (
              <div
                className="cm-empty cm-appear"
                style={{ "--stagger": "0ms" }}
              >
                Поки що немає коментарів
              </div>
            ) : !error ? (
              <>
                {roots.map((comment, i) =>
                  renderCommentBranch(comment, 0, i)
                )}
              </>
            ) : null}
          </div>
        )}

        <div
          className="cm-input cm-appear"
          style={{ "--stagger": `${(roots.length + 2) * 18}ms` }}
        >
          {replyTo && (
            <div className="cm-replyto">
              Відповідь для <b>@{replyTo.username}</b>
              <button
                className="cm-replyto__cancel"
                onClick={() => setReplyTo(null)}
              >
                скасувати
              </button>
            </div>
          )}

          <img className="cm-meAvatar" src={meAvatar || DEFAULT_AVATAR} alt="" />

          <div className="cm-inputWrap">
            <textarea
              ref={inputRef}
              className="cm-textarea cm-textarea--pill"
              placeholder="Напишіть коментар…"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              maxLength={2000}
            />

            <button
              className={`cm-sendCircle cm-sendCircle--in ${
                hasText ? "is-enabled" : "is-disabled"
              }`}
              disabled={!hasText || posting || !listingId}
              onClick={handlePost}
              aria-label="Надіслати"
            >
              <img src={sendArrow} alt="" />
            </button>
          </div>
        </div>
      </div>

      <Alerts
        open={confirmOpen}
        variant="deleteListing"
        title="Видалити коментар?"
        text="Видалити цей коментар і всі відповіді?"
        primaryText="Видалити"
        secondaryText={null}
        cancelText="Скасувати"
        tone="danger"
        onCancel={() => {
          pendingDeleteRef.current = null;
          setConfirmOpen(false);
        }}
        onClose={() => {
          pendingDeleteRef.current = null;
          setConfirmOpen(false);
        }}
        onPrimary={runConfirmedDelete}
      />
    </div>
  );

  if (typeof document === "undefined" || !document.body) return null;

  return createPortal(modalNode, document.body);
}